import SEO from "../components/SEO";
import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth, useTheme, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Sun, Moon, ArrowLeft, Store, Mail, Lock, MapPin, Phone, Link2, FileText, AlertCircle, User } from "lucide-react";
import AddressAutocomplete from "../components/AddressAutocomplete";
import ForgotPassword from "../components/ForgotPassword";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/qlurr1kb_9985D3C3-CBD5-4831-BB38-56FCD4CA4133.PNG";
const LIGHT_LOGO_URL = "https://customer-assets.emergentagent.com/job_f2aeab42-55b2-4611-9d0f-c73ce91bfc15/artifacts/ym9v5wop_7B92B142-D2AF-47C9-93FB-F3C3C1AB10EF.PNG";

export default function ShopAuth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("login");
  const [showForgotPassword, setShowForgotPassword] = useState(false);

  // Forced password change state — set when login succeeds with must_change_password=true
  const [forceChangeState, setForceChangeState] = useState(null); // { token, user, newPassword, confirmPassword, submitting }

  // Set initial tab from URL parameter
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam === "register" || tabParam === "login") {
      setActiveTab(tabParam);
    }
    if (searchParams.get("reset_token")) {
      setShowForgotPassword(true);
    }
  }, [searchParams]);

  // Login form state
  const [loginData, setLoginData] = useState({
    email: "",
    password: ""
  });

  // Register form state
  const [accountType, setAccountType] = useState("individual"); // "individual" | "franchise"
  const [registerData, setRegisterData] = useState({
    shop_name: "",
    contact_name: "",
    email: "",
    password: "",
    confirmPassword: "",
    address: null, // Structured address object
    phone: "",
    google_maps_link: "",
    description: ""
  });

  // Franchise owner register state (used when accountType === "franchise")
  const [franchiseRegisterData, setFranchiseRegisterData] = useState({
    owner_name: "",
    business_name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Try shop login first. If that 401s, fall back to franchise owner
      // login so the SAME login form works for both account types.
      let response;
      try {
        response = await axios.post(`${API}/auth/shop/login`, {
          email: loginData.email,
          password: loginData.password,
        });
      } catch (err) {
        if (err.response?.status === 401) {
          response = await axios.post(`${API}/auth/franchise-owner/login`, {
            email: loginData.email,
            password: loginData.password,
          });
        } else {
          throw err;
        }
      }

      // If this was a franchise owner login, route to the franchise dashboard.
      if (response.data.user_type === "franchise_owner") {
        login(response.data.access_token, response.data.user, "franchise_owner");
        toast.success(`Welcome back, ${response.data.user?.business_name || "owner"}!`);
        navigate("/franchise/dashboard");
        return;
      }

      // If admin issued a temp password, force a change before entering app.
      if (response.data.user?.must_change_password) {
        setForceChangeState({
          token: response.data.access_token,
          user: response.data.user,
          newPassword: "",
          confirmPassword: "",
          submitting: false,
        });
        toast.info("You're using a temporary password — please set a new one to continue.");
        setLoading(false);
        return;
      }

      login(response.data.access_token, response.data.user, response.data.user_type);
      toast.success("Welcome back!");
      navigate("/shop/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleForceChangePassword = async (e) => {
    e.preventDefault();
    if (!forceChangeState) return;
    const { newPassword, confirmPassword, token } = forceChangeState;
    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setForceChangeState((prev) => ({ ...prev, submitting: true }));
    try {
      await axios.post(
        `${API}/shop/change-password-forced`,
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const refreshedUser = { ...forceChangeState.user, must_change_password: false };
      login(token, refreshedUser, "shop");
      toast.success("Password updated — welcome to TyreMatch!");
      setForceChangeState(null);
      navigate("/shop/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to set new password");
      setForceChangeState((prev) => ({ ...prev, submitting: false }));
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();

    // ----- Franchise / multi-location owner branch -----
    if (accountType === "franchise") {
      const fr = franchiseRegisterData;
      if (!fr.owner_name || !fr.business_name || !fr.email || !fr.phone) {
        toast.error("Please complete all fields");
        return;
      }
      if (fr.password.length < 8) {
        toast.error("Password must be at least 8 characters");
        return;
      }
      if (fr.password !== fr.confirmPassword) {
        toast.error("Passwords do not match");
        return;
      }
      setLoading(true);
      try {
        const response = await axios.post(`${API}/auth/franchise-owner/register`, {
          owner_name: fr.owner_name,
          business_name: fr.business_name,
          email: fr.email,
          password: fr.password,
          phone: fr.phone,
        });
        login(response.data.access_token, response.data.user, "franchise_owner");
        toast.success(`Multi-location account created for ${fr.business_name}!`);
        navigate("/franchise/dashboard");
      } catch (error) {
        toast.error(error.response?.data?.detail || "Registration failed");
      } finally {
        setLoading(false);
      }
      return;
    }

    // ----- Individual shop branch (existing behaviour) -----
    if (registerData.password !== registerData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (!registerData.address || !registerData.address.full_address) {
      toast.error("Please enter your shop address");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/shop/register`, {
        shop_name: registerData.shop_name,
        contact_name: registerData.contact_name || null,
        email: registerData.email,
        password: registerData.password,
        address: registerData.address,
        phone: registerData.phone,
        google_maps_link: registerData.google_maps_link || null,
        description: registerData.description || null
      });

      login(response.data.access_token, response.data.user, response.data.user_type);
      toast.success("Shop registered! Your account is pending admin approval.");
      navigate("/shop/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO page="shopAuth" />

      {/* ---- Forced password change overlay (admin-issued temp password) ---- */}
      {forceChangeState && (
        <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur flex items-center justify-center p-4">
          <Card className="w-full max-w-md" data-testid="force-change-password-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-primary" />
                Set Your New Password
              </CardTitle>
              <CardDescription>
                You signed in with a temporary password. Please choose a new password to continue.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleForceChangePassword} className="space-y-4">
                <div>
                  <Label htmlFor="new-pwd">New password</Label>
                  <Input
                    id="new-pwd"
                    type="password"
                    minLength={8}
                    required
                    value={forceChangeState.newPassword}
                    onChange={(e) =>
                      setForceChangeState((prev) => ({ ...prev, newPassword: e.target.value }))
                    }
                    placeholder="At least 8 characters"
                    data-testid="force-new-password-input"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-pwd">Confirm new password</Label>
                  <Input
                    id="confirm-pwd"
                    type="password"
                    required
                    value={forceChangeState.confirmPassword}
                    onChange={(e) =>
                      setForceChangeState((prev) => ({ ...prev, confirmPassword: e.target.value }))
                    }
                    placeholder="Re-type your new password"
                    data-testid="force-confirm-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={forceChangeState.submitting}
                  className="w-full"
                  data-testid="force-submit-password-btn"
                >
                  {forceChangeState.submitting ? "Updating..." : "Set New Password & Continue"}
                </Button>
                <button
                  type="button"
                  onClick={() => setForceChangeState(null)}
                  className="w-full text-xs text-muted-foreground hover:text-foreground"
                >
                  Sign out
                </button>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Header */}
      <header className="p-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-5 w-5" />
          <span>Back to Home</span>
        </Link>
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="rounded-full"
          data-testid="theme-toggle"
        >
          {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          <div className="text-center mb-8">
            <Link to="/">
              <img src={theme === "dark" ? LOGO_URL : LIGHT_LOGO_URL} alt="TyreMatch" className="h-24 md:h-48 lg:h-56 mx-auto mb-6" />
            </Link>
            <h1 className="font-heading text-3xl font-bold mb-2">Tyre Shop Portal</h1>
            <p className="text-muted-foreground">
              Receive quote requests from local customers
            </p>
          </div>

          <Card className="glass-card" data-testid="shop-auth-card">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Register Shop</TabsTrigger>
              </TabsList>

              {/* Login Tab */}
              <TabsContent value="login">
                <CardHeader>
                  <CardTitle>{showForgotPassword ? "Reset Password" : "Shop Login"}</CardTitle>
                  <CardDescription>
                    {showForgotPassword ? "Reset your password" : "Access your shop dashboard"}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {showForgotPassword ? (
                    <ForgotPassword userType="shop" onBack={() => setShowForgotPassword(false)} />
                  ) : (
                  <form onSubmit={handleLogin} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="login-email"
                          type="email"
                          placeholder="shop@email.com"
                          className="pl-10"
                          value={loginData.email}
                          onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                          required
                          data-testid="shop-login-email-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="login-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="login-password"
                          type="password"
                          placeholder="••••••••"
                          className="pl-10"
                          value={loginData.password}
                          onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                          required
                          data-testid="shop-login-password-input"
                        />
                      </div>
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-primary text-primary-foreground"
                      disabled={loading}
                      data-testid="shop-login-submit-btn"
                    >
                      {loading ? "Logging in..." : "Login"}
                    </Button>
                    <Button
                      type="button"
                      variant="link"
                      className="w-full text-sm"
                      onClick={() => setShowForgotPassword(true)}
                      data-testid="forgot-password-link"
                    >
                      Forgot Password?
                    </Button>
                  </form>
                  )}
                </CardContent>
              </TabsContent>

              {/* Register Tab */}
              <TabsContent value="register">
                <CardHeader>
                  <CardTitle>Register Your Shop</CardTitle>
                  <CardDescription>
                    Join TyreMatch and start receiving leads
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleRegister} className="space-y-4">
                    {/* ---- Account type chooser ---- */}
                    <div className="space-y-2 rounded-lg border bg-muted/30 p-3" data-testid="account-type-chooser">
                      <Label className="text-sm font-medium">What type of account?</Label>
                      <div className="grid sm:grid-cols-2 gap-2">
                        <label
                          className={`flex items-start gap-3 p-3 rounded-md border-2 cursor-pointer transition-all ${
                            accountType === "individual" ? "border-primary bg-primary/5" : "border-input"
                          }`}
                          data-testid="account-type-individual"
                        >
                          <input
                            type="radio"
                            name="account_type"
                            value="individual"
                            checked={accountType === "individual"}
                            onChange={() => setAccountType("individual")}
                            className="mt-0.5 accent-primary"
                          />
                          <div>
                            <p className="text-sm font-medium">Individual Store</p>
                            <p className="text-[11px] text-muted-foreground">One email and one store</p>
                          </div>
                        </label>
                        <label
                          className={`flex items-start gap-3 p-3 rounded-md border-2 cursor-pointer transition-all ${
                            accountType === "franchise" ? "border-primary bg-primary/5" : "border-input"
                          }`}
                          data-testid="account-type-franchise"
                        >
                          <input
                            type="radio"
                            name="account_type"
                            value="franchise"
                            checked={accountType === "franchise"}
                            onChange={() => setAccountType("franchise")}
                            className="mt-0.5 accent-primary"
                          />
                          <div>
                            <p className="text-sm font-medium">Franchise / Multi-location</p>
                            <p className="text-[11px] text-muted-foreground">One email and multiple stores</p>
                          </div>
                        </label>
                      </div>
                    </div>

                    {/* ---- Franchise owner fields ---- */}
                    {accountType === "franchise" && (
                      <div className="space-y-4" data-testid="franchise-register-fields">
                        <div className="space-y-2">
                          <Label htmlFor="franchise-owner-name">Owner Name</Label>
                          <Input
                            id="franchise-owner-name"
                            type="text"
                            placeholder="Your full name"
                            value={franchiseRegisterData.owner_name}
                            onChange={(e) => setFranchiseRegisterData({ ...franchiseRegisterData, owner_name: e.target.value })}
                            required
                            data-testid="franchise-owner-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="franchise-business-name">Business / Franchise Group Name</Label>
                          <Input
                            id="franchise-business-name"
                            type="text"
                            placeholder="e.g. Bridgestone NZ"
                            value={franchiseRegisterData.business_name}
                            onChange={(e) => setFranchiseRegisterData({ ...franchiseRegisterData, business_name: e.target.value })}
                            required
                            data-testid="franchise-business-name-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="franchise-email">Email (master login)</Label>
                          <Input
                            id="franchise-email"
                            type="email"
                            placeholder="owner@business.co.nz"
                            value={franchiseRegisterData.email}
                            onChange={(e) => setFranchiseRegisterData({ ...franchiseRegisterData, email: e.target.value })}
                            required
                            data-testid="franchise-email-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="franchise-phone">Phone</Label>
                          <Input
                            id="franchise-phone"
                            type="tel"
                            placeholder="+64 21 123 4567"
                            value={franchiseRegisterData.phone}
                            onChange={(e) => setFranchiseRegisterData({ ...franchiseRegisterData, phone: e.target.value })}
                            required
                            data-testid="franchise-phone-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="franchise-password">Password</Label>
                          <Input
                            id="franchise-password"
                            type="password"
                            placeholder="Min 8 characters"
                            value={franchiseRegisterData.password}
                            onChange={(e) => setFranchiseRegisterData({ ...franchiseRegisterData, password: e.target.value })}
                            required
                            data-testid="franchise-password-input"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="franchise-password-confirm">Confirm Password</Label>
                          <Input
                            id="franchise-password-confirm"
                            type="password"
                            value={franchiseRegisterData.confirmPassword}
                            onChange={(e) => setFranchiseRegisterData({ ...franchiseRegisterData, confirmPassword: e.target.value })}
                            required
                            data-testid="franchise-password-confirm-input"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          You'll be able to add your stores after logging in.
                        </p>
                        <Button
                          type="submit"
                          className="w-full"
                          disabled={loading}
                          data-testid="franchise-register-submit"
                        >
                          {loading ? "Creating account..." : "Create Multi-location Account"}
                        </Button>
                      </div>
                    )}

                    {/* ---- Individual shop fields (original) ---- */}
                    {accountType === "individual" && (
                    <>
                    <div className="space-y-2">
                      <Label htmlFor="register-shop-name">Shop Name</Label>
                      <div className="relative">
                        <Store className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="register-shop-name"
                          type="text"
                          placeholder="Your Tyre Shop Name"
                          className="pl-10"
                          value={registerData.shop_name}
                          onChange={(e) => setRegisterData({ ...registerData, shop_name: e.target.value })}
                          required
                          data-testid="shop-register-name-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-contact-name">Contact Name</Label>
                      <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="register-contact-name"
                          type="text"
                          placeholder="Full name of primary contact"
                          className="pl-10"
                          value={registerData.contact_name}
                          onChange={(e) => setRegisterData({ ...registerData, contact_name: e.target.value })}
                          data-testid="shop-register-contact-name-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-email">Business Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="register-email"
                          type="email"
                          placeholder="shop@email.com"
                          className="pl-10"
                          value={registerData.email}
                          onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                          required
                          data-testid="shop-register-email-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-address">Shop Address *</Label>
                      <AddressAutocomplete
                        value={registerData.address}
                        onChange={(address) => setRegisterData({ ...registerData, address })}
                        placeholder="Start typing your shop address..."
                        required
                      />
                      <p className="text-xs text-muted-foreground">
                        Your address helps customers find you
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-phone">Phone *</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="register-phone"
                          type="tel"
                          placeholder="09 123 4567"
                          className="pl-10"
                          value={registerData.phone}
                          onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                          required
                          data-testid="shop-register-phone-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-google-link">Google Maps Link (Optional)</Label>
                      <div className="relative">
                        <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="register-google-link"
                          type="url"
                          placeholder="https://maps.google.com/..."
                          className="pl-10"
                          value={registerData.google_maps_link}
                          onChange={(e) => setRegisterData({ ...registerData, google_maps_link: e.target.value })}
                          data-testid="shop-register-google-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-description">Shop Description (Optional)</Label>
                      <Textarea
                        id="register-description"
                        placeholder="Tell customers about your shop..."
                        value={registerData.description}
                        onChange={(e) => setRegisterData({ ...registerData, description: e.target.value })}
                        rows={3}
                        data-testid="shop-register-description-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="register-password"
                          type="password"
                          placeholder="••••••••"
                          className="pl-10"
                          value={registerData.password}
                          onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                          required
                          data-testid="shop-register-password-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-confirm">Confirm Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="register-confirm"
                          type="password"
                          placeholder="••••••••"
                          className="pl-10"
                          value={registerData.confirmPassword}
                          onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
                          required
                          data-testid="shop-register-confirm-input"
                        />
                      </div>
                    </div>
                    <Button
                      type="submit"
                      className="w-full bg-primary text-primary-foreground"
                      disabled={loading}
                      data-testid="shop-register-submit-btn"
                    >
                      {loading ? "Registering..." : "Register Shop"}
                    </Button>
                    </>
                    )}
                  </form>
                </CardContent>
              </TabsContent>
            </Tabs>
          </Card>

          <p className="text-center text-sm text-muted-foreground mt-6">
            Looking to get quotes?{" "}
            <Link to="/customer/auth" className="text-primary hover:underline">
              Customer Login
            </Link>
          </p>
        </div>
      </main>
    </div>
  );
}
